import path from "node:path";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import { Command } from "commander";
import { bareSoftDepName, colorRef, logErr, S } from "../helpers.ts";

/**
 * Split a dependency ref into its `kind` + `name`, tolerant of both the bare
 * `kind:name` form the user is likely to type and the canonical
 * `kind:@publisher/name#version` form. `kind` is `undefined` when the ref
 * carries no `kind:` prefix — matching then falls back to name-only.
 */
function refKindName(ref: string): { kind?: string; name: string } {
  const trimmed = ref.trim();
  const colon = trimmed.indexOf(":");
  let kind = colon === -1 ? undefined : trimmed.slice(0, colon);
  // `parseAssetRef` normalizes the `mcp` shorthand to `mcp-server`, so declared
  // mcp servers surface under that kind — mirror it or the match wrongly misses.
  if (kind === "mcp") kind = "mcp-server";
  let rest = colon === -1 ? trimmed : trimmed.slice(colon + 1);
  const hash = rest.indexOf("#");
  if (hash !== -1) rest = rest.slice(0, hash);
  if (rest.startsWith("@")) {
    const slash = rest.indexOf("/");
    if (slash !== -1) rest = rest.slice(slash + 1);
  }
  return { kind, name: rest };
}

/**
 * Pick the right message when `am.why(ref)` returns an empty chain. Two
 * distinct causes need different guidance:
 *
 * - The ref **is** a declared dependency (matches `declared` by kind+name) but
 *   isn't in the lock file yet → tell the user to run `skaile install`.
 * - The ref is **not** declared at all → `skaile install` cannot help, so say
 *   plainly that nothing in `skaile.yaml` requires it.
 *
 * Pure and terminal-free so it can be unit-tested; the command wires the
 * returned string into `logErr`.
 *
 * @param declared - Declared deps exposing `.kind` + `.name` (from `listDeclared`).
 */
export function whyEmptyMessage(
  ref: string,
  declared: Array<{ kind: string; name: string }>,
): string {
  const { kind, name } = refKindName(ref);
  const isDeclared = declared.some(
    (d) => d.name === name && (kind === undefined || d.kind === kind),
  );
  if (isDeclared) {
    return `${ref} not found in lock file. Run \`skaile install\` first.`;
  }
  return `${ref} is not a dependency of this project — nothing in skaile.yaml requires it. Run \`skaile list\` to see declared dependencies.`;
}

/**
 * Creates the `skaile why` command.
 *
 * Shows why an asset is installed by printing its dependency chain from the
 * top-level skaile.yaml requirement down to the asset.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#why
 */
export function makeWhyCommand(): Command {
  return new Command("why")
    .description("Show why an asset is installed (dependency chain)")
    .argument("<ref>", "Asset reference (kind:name)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (ref: string, opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      const chain = am.why(ref);
      if (chain.length === 0) {
        // Best-effort: if the declared deps can't be read for any reason (no
        // skaile.yaml, a legacy-key manifest error, …) we can't tell "declared"
        // from "undeclared", so keep the generic install hint rather than crash.
        let declared: Array<{ kind: string; name: string }>;
        try {
          declared = am.listDeclared();
        } catch {
          logErr(`${ref} not found in lock file. Run \`skaile install\` first.`);
          return;
        }
        logErr(whyEmptyMessage(ref, declared));
        return;
      }
      console.log();
      console.log(S.heading("  Dependency chain"));
      for (let i = 0; i < chain.length; i++) {
        const indent = "  ".repeat(i);
        const arrow = i === 0 ? "" : "← ";
        console.log(`  ${indent}${arrow}${colorRef(chain[i])}`);
      }
      // Inferred body-link soft deps this asset pulls (report-only, distinct from
      // the typed dependency chain above).
      const softDeps = await am.softDepsOf([ref]);
      if (softDeps.length > 0) {
        console.log(
          `  ${S.dim(`also pulls (soft): ${softDeps.map((e) => bareSoftDepName(e.to)).join(", ")}`)}`,
        );
      }
      console.log();
    });
}
